import angular from 'angular';
import { TipoArquivosService } from '../../../TipoArquivos/tipo-arquivos.service';
import {
    IConfiguracaoDocumentoForm,
} from '../models/configuracaodocumento.model';
import {
    ITipoArquivoCreatePayload,
    ITipoArquivoResponse,
    ITipoArquivoUpdatePayload
} from '../../../TipoArquivos/models/tipo-arquivos.service.model';
import { ConfiguracaoDocumentosModalMode } from './configuracaodocumentos.modal.service';

export class ConfiguracaoDocumentosModalController implements angular.IController {
    static $inject = ['entity', 'mode', '$uibModalInstance', 'TipoArquivosService', 'NewToaster'];

    public documentType: IConfiguracaoDocumentoForm;
    public originalTitle: string;
    public form: angular.IFormController;
    public mode: ConfiguracaoDocumentosModalMode;
    public busy = false;
    public readonly codigoPattern = /^[A-Za-z0-9_]+$/;
    public readonly codigoMaxLength = 100;
    public readonly tituloMaxLength = 255;
    public readonly descricaoMaxLength = 255;

    constructor(
        entity: IConfiguracaoDocumentoForm,
        mode: ConfiguracaoDocumentosModalMode,
        private $uibModalInstance: ng.ui.bootstrap.IModalInstanceService,
        private tipoArquivosService: TipoArquivosService,
        private NewToaster: any,
    ) {
        this.mode = mode;
        this.documentType = this.buildInitialDocument(entity);
        this.originalTitle = this.documentType.titulo;
    }

    public onCodigoChange(): void {
        if (typeof this.documentType.codigo !== 'string') {
            return;
        }

        this.documentType.codigo = this.documentType.codigo.toUpperCase();
    }

    public onTituloChange(): void {
        this.validateTituloField();
    }

    public onToggleAssinatura(): void {
        if (!this.documentType.assinatura) {
            this.documentType.prazoassinaturapadrao = null;
        }

        this.validatePrazoAssinaturaField();
    }

    public onPrazoAssinaturaChange(): void {
        this.validatePrazoAssinaturaField();
    }

    public salvar(form?: angular.IFormController): void {
        this.validateTituloField();
        this.validatePrazoAssinaturaField();

        if (!this.isValid(form)) {
            return;
        }

        this.busy = true;

        const request = this.mode === 'create'
            ? this.tipoArquivosService.create(this.buildCreatePayload())
            : this.submitUpdate();

        request
            .then((documentType) => {
                this.NewToaster.pop({
                    type: 'success',
                    title: this.mode === 'create'
                        ? 'Tipo de documento criado com sucesso!'
                        : 'Tipo de documento atualizado com sucesso!'
                });

                this.$uibModalInstance.close(documentType);
            })
            .catch((error) => {
                const errorMessage = error?.data?.message || error?.message || 'Ocorreu um erro ao salvar o tipo de documento.';

                if (!error?.handledByController) {
                    this.NewToaster.pop({
                        type: 'error',
                        title: this.mode === 'create'
                            ? 'Erro ao criar tipo de documento'
                            : 'Erro ao atualizar tipo de documento',
                        body: errorMessage
                    });
                }
            })
            .finally(() => {
                this.busy = false;
            });
    }

    public cancelar(): void {
        this.$uibModalInstance.dismiss('cancel');
    }

    public canShowRegistrationFields(): boolean {
        return this.mode === 'create' || !this.documentType.tipopadrao;
    }

    public canEditCodigoField(): boolean {
        return this.mode === 'create';
    }

    public modalTitle(): string {
        if (this.mode === 'create') {
            return 'Criar tipo de documento';
        }

        return `Editar ${this.originalTitle || 'tipo de documento'}`;
    }

    public submitLabel(): string {
        return this.mode === 'create' ? 'Criar tipo de documento' : 'Salvar alterações';
    }

    private buildInitialDocument(entity: IConfiguracaoDocumentoForm): IConfiguracaoDocumentoForm {
        const documentType: Partial<IConfiguracaoDocumentoForm> = entity ? angular.copy(entity) : {};

        return {
            ...documentType,
            codigo: documentType.codigo || '',
            titulo: documentType.titulo || '',
            descricao: documentType.descricao || '',
            exigeliberacao: documentType.exigeliberacao ?? false,
            assinatura: documentType.assinatura ?? false,
            prazoassinaturapadrao: documentType.prazoassinaturapadrao ?? null,
            tipopadrao: documentType.tipopadrao ?? false,
        };
    }

    private submitUpdate(): angular.IPromise<ITipoArquivoResponse> {
        const identifier = this.documentType.tipoarquivo;
        if (!identifier) {
            const errorMessage = 'Não foi possível identificar o tipo de documento a ser atualizado.';

            this.NewToaster.pop({
                type: 'error',
                title: 'Erro ao atualizar tipo de documento',
                body: errorMessage
            });

            this.busy = false;
            return this.tipoArquivosService.$q.reject({
                handledByController: true,
                message: errorMessage,
            });
        }

        return this.tipoArquivosService.update(identifier, this.buildUpdatePayload());
    }

    private buildCreatePayload(): ITipoArquivoCreatePayload {
        return this.buildBasePayload();
    }

    private buildUpdatePayload(): ITipoArquivoUpdatePayload {
        const payload: ITipoArquivoUpdatePayload = {
            exigeliberacao: !!this.documentType.exigeliberacao,
            assinatura: !!this.documentType.assinatura,
            prazoassinaturapadrao: this.normalizePrazoAssinatura(),
        };

        if (!this.documentType.tipopadrao) {
            const registrationFieldsPayload = this.buildRegistrationFieldsPayload();
            payload.titulo = registrationFieldsPayload.titulo;
            payload.descricao = registrationFieldsPayload.descricao;
        }

        return payload;
    }

    private buildBasePayload(): ITipoArquivoCreatePayload {
        const registrationFieldsPayload = this.buildRegistrationFieldsPayload();

        return {
            codigo: (this.documentType.codigo || '').trim().toUpperCase(),
            ...registrationFieldsPayload,
            exigeliberacao: !!this.documentType.exigeliberacao,
            assinatura: !!this.documentType.assinatura,
            prazoassinaturapadrao: this.normalizePrazoAssinatura(),
        };
    }

    private buildRegistrationFieldsPayload(): Pick<ITipoArquivoCreatePayload, 'titulo' | 'descricao'> {
        const titulo = (this.documentType.titulo || '').trim();
        const descricao = (this.documentType.descricao || '').trim();

        return {
            titulo,
            descricao,
        };
    }

    private isValid(form?: angular.IFormController): boolean {
        if (form && form.$invalid) {
            return false;
        }

        return this.isPrazoAssinaturaValido();
    }

    private validateTituloField(): void {
        if (!this.form?.titulo?.$setValidity || !this.canShowRegistrationFields()) {
            return;
        }

        this.form.titulo.$setValidity('trimmedRequired', !!(this.documentType.titulo || '').trim());
    }

    private validatePrazoAssinaturaField(): void {
        if (!this.form?.prazoAssinaturaDias?.$setValidity) {
            return;
        }

        this.form.prazoAssinaturaDias.$setValidity('integer', this.isPrazoAssinaturaValido());
    }

    private normalizePrazoAssinatura(): number | null {
        if (!this.documentType.assinatura) {
            return null;
        }

        const prazo = this.documentType.prazoassinaturapadrao;
        if (prazo === null || prazo === undefined) {
            return null;
        }

        return Number(prazo);
    }

    private isPrazoAssinaturaValido(): boolean {
        if (!this.documentType.assinatura) {
            return true;
        }

        const prazo = this.normalizePrazoAssinatura();
        if (prazo === null || prazo === undefined) {
            return true;
        }

        return !isNaN(prazo) && prazo >= 0 && Math.floor(prazo) === prazo;
    }
}
